import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { useAuth } from './auth'
import type { OrganizationDetail } from './types'

/**
 * The email-address rule, mirrored from the server (src/api/email_address.py,
 * the one place it is defined). The server decides; this only gives a faster
 * message before the request is sent.
 *
 * Valid: exactly one "@"; local part 1-64 characters; domain has a dot (and
 * does not start or end with one); no spaces or control characters; none of
 * < > , ; "; at most 254 characters in total.
 */
export function isValidEmailAddress(raw: string): boolean {
  const address = raw.trim().toLowerCase()
  if (!address || address.length > 254) return false
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001f\u007f<>,;"]/.test(address)) return false
  const parts = address.split('@')
  if (parts.length !== 2) return false
  const [local, domain] = parts
  if (local.length < 1 || local.length > 64) return false
  if (!domain.includes('.') || domain.startsWith('.') || domain.endsWith('.')) return false
  return true
}

/** True when the global email switch is on for this deployment. */
export function useEmailFeature(): boolean {
  const { user } = useAuth()
  return user?.feature_email_channel === true
}

/**
 * Whether this organisation has finished setting up email: a contact name and
 * a reply-to address. Only fetched while the global switch is on.
 */
export function useEmailSetup() {
  const emailOn = useEmailFeature()
  const { data, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: () => api.get<OrganizationDetail>('/api/v1/organizations/me'),
    enabled: emailOn,
  })
  const hasContact = Boolean(data?.email_contact_name?.trim())
  const hasReplyTo = Boolean(data?.reply_to_email?.trim())
  return {
    emailOn,
    isLoading,
    org: data,
    // Both fields are set (the switch itself is separate: org.email_enabled).
    detailsSet: hasContact && hasReplyTo,
    switchedOn: data?.email_enabled === true,
  }
}
